import { useEffect, useState } from "react";
import api from "../../services/api";
import {
  Users, BookOpen, TrendingUp, Activity, Clock, WalletCards
} from "lucide-react";

import { StatCard, Skeleton, Card, Badge } from "../../components/ui";
import ChartWidget from "../../components/advanced/ChartWidget";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats")
      .then(res => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const cards = stats ? [
    { label: "Total Institutes", value: stats.total_institutes || 0, icon: Users, variant: "gradient", gradient: "from-blue-600 to-indigo-600" },
    { label: "Active Institutes", value: stats.active_institutes || 0, icon: Activity, variant: "elevated" },
    { label: "Platform Revenue", value: `₹${stats.total_revenue?.toLocaleString() || 0}`, icon: TrendingUp, variant: "glass" },
    { label: "Global Users", value: stats.total_platform_users || 0, icon: Users, variant: "default" },
    { label: "Total Courses", value: stats.total_courses || 0, icon: BookOpen, variant: "glass" },
    { label: "SaaS Subscriptions", value: stats.active_subscriptions || 0, icon: WalletCards, variant: "elevated" },
  ] : [];

  return (
    <div className="space-y-8 font-['Inter',sans-serif]">
      <header>
        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Admin Console</h1>
        <p className="text-slate-500 mt-1">Platform overview and SaaS tenant monitoring.</p>
      </header>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
        {loading
          ? [1, 2, 3, 4, 5, 6].map(i => <Skeleton.Card key={i} />)
          : cards.map(c => <StatCard key={c.label} title={c.label} value={c.value} icon={c.icon} variant={c.variant} gradient={c.gradient} />)
        }
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Revenue Chart */}
        <div className="xl:col-span-2">
          {loading ? <Skeleton className="h-full min-h-[300px] rounded-[2rem]" /> : (
            <ChartWidget 
              title="Platform Revenue" 
              subtitle="Monthly recurring revenue across all tenants"
              data={stats?.revenue_chart || []}
              dataKey="revenue"
              xAxisKey="month"
              color="#4f46e5"
              height={280}
            />
          )}
        </div>

        {/* Activity Feed */}
        <Card variant="glass" className="h-full">
          <Card.Header title="Recent Activity" subtitle="Real-time platform logs" />
          <Card.Body className="space-y-4">
            {loading ? (
              [1, 2, 3].map(i => (
                <div key={i} className="flex gap-4 animate-pulse">
                  <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-800 shrink-0" />
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-4 bg-slate-200 dark:bg-slate-800 rounded w-3/4" />
                    <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded w-1/4" />
                  </div>
                </div>
              ))
            ) : stats?.recent_activity?.length > 0 ? (
              stats.recent_activity.map((act, i) => (
                <div key={i} className="flex gap-4 group items-start">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center shrink-0 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors shadow-sm">
                    <Clock size={16} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{act.msg}</p>
                    <p className="text-xs font-semibold text-slate-400 mt-0.5">{act.time}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-slate-500 font-bold text-sm py-8">No recent activity</p>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* SaaS Plans Breakdown */}
      <Card variant="default">
        <Card.Header title="SaaS Plans" subtitle="Active tenants by subscription tier" />
        <Card.Body>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats?.saas_breakdown?.length > 0 ? stats.saas_breakdown.map((item) => (
              <div key={item.plan_name} className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-5 hover:bg-white dark:hover:bg-slate-800 transition-colors shadow-sm">
                <Badge variant="primary" className="mb-3">{item.plan_name}</Badge>
                <div className="flex items-baseline gap-2">
                  <p className="text-4xl font-black tracking-tight text-slate-900 dark:text-white">{item.count}</p>
                  <p className="text-xs font-bold text-slate-500 uppercase">Tenants</p>
                </div>
              </div>
            )) : (
              <div className="col-span-full py-12 flex items-center justify-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                <p className="text-slate-500 font-bold text-sm">No active SaaS subscriptions found.</p>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
